/**
 * auth.service.js
 *
 * AuthService: all authentication-related API calls
 * - Login, Register, Logout
 * - Token Refresh
 * - Password Reset (forgot/reset)
 * - Email verification
 * - Password change
 */

import apiClient from "./apiClient";

const unwrapAuthPayload = (response) => response?.data || response;

// Tokens are now stored in httpOnly cookies set by the server — they must NOT
// be written to localStorage. Only non-sensitive profile data is stored locally.
const persistAuthPayload = (payload) => {
  if (!payload) return payload;
  if (payload.user) localStorage.setItem("user", JSON.stringify(payload.user));
  return payload;
};

const AuthService = {
  /**
   * Login user with email/password
   * @param {Object} credentials { email, password }
   * @returns {Promise<Object>} user data + tokens
   */
  login: async (credentials) => {
    const { data } = await apiClient.post("/auth/login", credentials);
    return persistAuthPayload(unwrapAuthPayload(data));
  },

  // ── MFA (TOTP) ──────────────────────────────────────────────────────────────
  // Second login step for an enrolled user. body: { mfaToken, code } | { mfaToken, recoveryCode }
  loginVerifyMfa: async (body) => {
    const { data } = await apiClient.post("/auth/mfa/login-verify", body);
    return persistAuthPayload(unwrapAuthPayload(data));
  },
  // Enrolment status for the Security settings panel.
  mfaStatus: async () => unwrapAuthPayload((await apiClient.get("/auth/mfa/status")).data),
  // Begin enrolment → { secret, otpauthUrl }. Accepts an optional mfaToken (forced enrol).
  mfaSetup: async (body = {}) => unwrapAuthPayload((await apiClient.post("/auth/mfa/setup", body)).data),
  // Confirm the first code. Voluntary → { recoveryCodes }. Forced → also a session (persisted).
  mfaVerifySetup: async (body) => {
    const { data } = await apiClient.post("/auth/mfa/verify-setup", body);
    return persistAuthPayload(unwrapAuthPayload(data));
  },
  // Turn MFA off. body: { code }
  mfaDisable: async (body) => (await apiClient.post("/auth/mfa/disable", body)).data,
  // Admin (university_admin/super_admin): email a user a 2FA setup link.
  mfaInvite: async (userId) => (await apiClient.post("/auth/mfa/admin/invite", { userId })).data,
  // Admin: email a setup link to every un-enrolled student/teacher in the
  // institution. super_admin must pass a universityId; university_admin omits it.
  mfaInviteUnenrolled: async (universityId) =>
    (await apiClient.post("/auth/mfa/admin/invite-unenrolled", universityId ? { universityId } : {})).data,

  /**
   * Register new user
   * @param {Object} data { email, password, firstName, lastName, ... }
   */
  register: async (data) => {
    const { data: response } = await apiClient.post("/auth/register", data);
    return persistAuthPayload(unwrapAuthPayload(response));
  },

  /**
   * Register a new institution (university_admin path)
   * Creates University + university_admin user atomically.
   * Returns { accessToken, refreshToken, user, university, requiresBilling: true }
   */
  registerInstitution: async (data) => {
    const { data: response } = await apiClient.post("/auth/register-institution", data);
    return persistAuthPayload(unwrapAuthPayload(response));
  },

  /**
   * Logout current user — refreshToken cookie sent automatically by browser
   */
  logout: async () => {
    const { data } = await apiClient.post("/auth/logout");
    return data;
  },

  /**
   * Fetch current user from server (validates httpOnly cookie session)
   */
  getMe: async () => {
    const { data } = await apiClient.get("/auth/me");
    return data;
  },

  /**
   * Refresh access token using refresh token
   * @param {string} refreshToken
   */
  refreshToken: async (refreshToken) => {
    const { data } = await apiClient.post("/auth/refresh-token", { refreshToken });
    return persistAuthPayload(unwrapAuthPayload(data));
  },

  /**
   * Request password reset email
   * @param {string} email
   */
  forgotPassword: async (email) => {
    const { data } = await apiClient.post("/auth/forgot-password", { email });
    return data;
  },

  /**
   * Reset password using OTP
   * @param {string} email
   * @param {string} otp    6-digit code from email
   * @param {string} newPassword
   */
  resetPassword: async (email, otp, newPassword) => {
    const { data } = await apiClient.post("/auth/reset-password", { email, otp, newPassword });
    return data;
  },

  /**
   * Verify email using token
   */
  verifyEmail: async (token) => {
    const { data } = await apiClient.get(`/auth/verify-email/${token}`);
    return data;
  },

  /**
   * Resend verification email
   */
  resendVerification: async (email) => {
    const { data } = await apiClient.post("/auth/resend-verification", { email });
    return data;
  },

  /**
   * Change password for logged-in user
   */
  changePassword: async (payload) => {
    const { data } = await apiClient.post("/auth/change-password", payload);
    return data;
  },
};

export default AuthService;
